/**
 * The client-side half of the Thank You Notes feed: the one filter the server cannot run, plus the
 * chip and CSV presentation.
 *
 * Since GFG-234 search, campaign and the submitted date range are SQL in `GET /thank-you-notes` —
 * they travel as query params (`ThankYouNotesQuery`) and never reach this file. Favorites stay
 * here because they are session state the server knows nothing about: it always answers
 * `favorite: false`, so `favoriteIds` is the only source of truth. The trade-off is that
 * `favoritesOnly` can only narrow the page already fetched, not the whole result set.
 */
import { DATE_LOCALE } from "@/constants/dates";
import type { ThankYouNote, ThankYouNoteClientFilters } from "@/interfaces/thankYouNotes";
import type { CsvColumn } from "@/lib/csv";
import { formatDateLong } from "@/utils/date-format";

/**
 * Chip-only short date ("Jun 1", no year) — private because the rest of the feature (CSV export)
 * uses `formatDateLong`. Parses a bare `YYYY-MM-DD` as UTC noon rather than local midnight, same
 * fix `date-format.ts` applies: local midnight shifts a day earlier in any negative-offset
 * timezone (e.g. "2026-06-01" would render as "May 31").
 */
function shortDate(iso: string): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  // timeZone: "UTC" pins the rendered day regardless of the runtime's offset — the UTC-noon parse
  // above only guards against negative offsets; without this, positive offsets >= UTC+12 (e.g.
  // Pacific/Kiritimati, UTC+14) still roll the date forward a day.
  return date.toLocaleDateString(DATE_LOCALE, { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * Keeps the notes the session has favorited; a false `favoritesOnly` lets everything through.
 * Reads `favoriteIds` — the session's source of truth — and never each note's own `favorite`
 * field, which the server always sends as `false` because favorites are not persisted.
 */
export function filterByFavorites(
  notes: ThankYouNote[],
  f: ThankYouNoteClientFilters,
): ThankYouNote[] {
  if (!f.favoritesOnly) return notes;
  return notes.filter((note) => f.favoriteIds.has(note.id));
}

/**
 * The submitted-range filter chip's label: both bounds → "Jun 1 – Jun 30" (en dash), only one
 * bound → "from Jun 1" / "until Jun 30", neither → "" (no chip to show). Deliberately year-less
 * (per ticket acceptance criteria) — uses the local `shortDate`, not `formatDateLong`.
 */
export function submittedChipLabel(from: string | null, to: string | null): string {
  if (from && to) return `${shortDate(from)} – ${shortDate(to)}`;
  if (from) return `from ${shortDate(from)}`;
  if (to) return `until ${shortDate(to)}`;
  return "";
}

/**
 * The ticket's 7 CSV columns, in order. Callers pass already-hydrated notes (each `favorite`
 * reflecting the session's favorites Set), so this reads `row.favorite` directly rather than
 * taking a separate favorites lookup.
 */
export const thankYouNotesCsvColumns: CsvColumn<ThankYouNote>[] = [
  { label: "message", value: (row) => row.message },
  { label: "recipient", value: (row) => row.recipient },
  { label: "campaign", value: (row) => row.campaign },
  { label: "date_submitted", value: (row) => formatDateLong(row.submitted) },
  { label: "date_redeemed", value: (row) => formatDateLong(row.redeemed) },
  { label: "gift", value: (row) => row.giftName ?? "—" },
  { label: "favorite", value: (row) => (row.favorite ? "yes" : "no") },
];
